import { useState, useEffect, useCallback } from 'react';
import type { UserGearItem } from '@nfa/types';
import * as api from '../lib/api';

const SESSION_TOKEN_KEY = 'nfa_session_token';

interface UseGearClosetResult {
  items: UserGearItem[];
  isLoading: boolean;
  error: string | null;
  addItem: (data: api.AddGearRequest) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  updateItem: (itemId: string, data: { notes?: string; acquired_date?: string }) => Promise<void>;
  checkOwned: (productIds: string[]) => Promise<Record<string, boolean>>;
  refresh: () => Promise<void>;
}

function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function useGearCloset(): UseGearClosetResult {
  const [items, setItems] = useState<UserGearItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGear = useCallback(async () => {
    const token = getSessionToken();
    if (!token) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const { items } = await api.getGear(token);
      setItems(items);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        // Not authenticated - clear items
        setItems([]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load gear');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGear();
  }, [fetchGear]);

  const addItem = useCallback(async (data: api.AddGearRequest) => {
    const token = getSessionToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const { item } = await api.addGear(token, data);
    setItems(prev => [item, ...prev]);
  }, []);

  const removeItem = useCallback(async (itemId: string) => {
    const token = getSessionToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    await api.removeGear(token, itemId);
    setItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const updateItem = useCallback(async (
    itemId: string,
    data: { notes?: string; acquired_date?: string }
  ) => {
    const token = getSessionToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const { item } = await api.updateGear(token, itemId, data);
    setItems(prev => prev.map(i => i.id === itemId ? item : i));
  }, []);

  const checkOwned = useCallback(async (productIds: string[]): Promise<Record<string, boolean>> => {
    const token = getSessionToken();
    if (!token) {
      // If not authenticated, return all false
      return Object.fromEntries(productIds.map(id => [id, false]));
    }

    const { owned } = await api.checkOwnedGear(token, productIds);
    return owned;
  }, []);

  return {
    items,
    isLoading,
    error,
    addItem,
    removeItem,
    updateItem,
    checkOwned,
    refresh: fetchGear,
  };
}
